import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Briefcase, ExternalLink, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import BusinessNameTypeahead from "@/components/BusinessNameTypeahead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Customer lookup for tasks — same typeahead as quote jobs (pick existing or type a new business).
export const TaskCustomerField = ({ form, setForm }) => (
  <div data-testid="task-customer-field">
    <BusinessNameTypeahead
      value={form.client?.business_name || form.client?.contact_name || ""}
      customerId={form.customer_id}
      onSelectExisting={(c) =>
        setForm((f) => ({
          ...f,
          customer_id: c.id,
          client: { business_name: c.business_name || "", contact_name: c.contact_name || "", email: c.email || "", phone: c.phone || "" },
        }))
      }
      onCreateNew={(name) => setForm((f) => ({ ...f, customer_id: null, client: { business_name: name } }))}
      onChange={(v) => setForm((f) => ({ ...f, customer_id: null, client: { business_name: v } }))}
      onClear={() => setForm((f) => ({ ...f, customer_id: null, client: {} }))}
    />
  </div>
);

const LinkedChip = ({ link, onClear }) => (
  <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm" data-testid="task-linked-job">
    <Briefcase className="w-4 h-4 text-indigo-600 shrink-0" />
    <a
      href={`/quote?job=${link.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex-1 min-w-0 truncate font-medium text-indigo-700 hover:underline"
      data-testid="task-linked-job-open"
    >
      {link.job_number ? `${link.job_number} · ` : ""}{link.job_name || "Untitled job"}
      <ExternalLink className="inline w-3 h-3 ml-1 -mt-0.5" />
    </a>
    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={onClear} data-testid="task-linked-job-clear">
      <X className="w-3.5 h-3.5" />
    </Button>
  </div>
);

// Optional reference to an existing (non-task) job, searched by job number or name.
export const TaskJobLinkField = ({ value, onChange, excludeId }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) return setResults([]);
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/jobs`, { params: { search: q.trim(), limit: 15, include_archived: true } });
        const list = Array.isArray(res.data) ? res.data : res.data?.jobs || [];
        setResults(list.filter((j) => j.job_data?.job_type !== "task" && j.id !== excludeId).slice(0, 8));
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, excludeId]);

  if (value) return <LinkedChip link={value} onClear={() => onChange(null)} />;

  return (
    <div className="relative" ref={ref}>
      <Input
        placeholder="Search by job number or name..."
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        data-testid="task-job-link-input"
      />
      {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
      {open && q.trim().length >= 2 && !loading && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="task-job-link-results">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">No matching jobs</div>
          ) : (
            results.map((j) => (
              <button
                type="button"
                key={j.id}
                onClick={() => { onChange({ id: j.id, job_number: j.job_number || "", job_name: j.job_name || "" }); setQ(""); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex gap-2"
                data-testid={`task-job-link-option-${j.id}`}
              >
                <span className="font-mono text-xs text-gray-500 shrink-0 pt-0.5">{j.job_number}</span>
                <span className="truncate text-gray-800">{j.job_name || "Untitled"}</span>
                <span className="ml-auto text-[11px] text-gray-400 shrink-0">{j.status}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
